import React, { useEffect, useMemo, useRef, useState } from "react";
import { store } from "../../store";
import { StatisticCard } from "@/components/ui/statistic-card";
import { ChartLineIcon, ContactIcon, Forklift, TriangleAlert, Users } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { StandardPieChart } from "@/components/ui/standard-pie-chart";
import { StandardTimelineChart } from "@/components/ui/standard-timeline-chart";
import {
    List,
    ListContent,
    ListDescription,
    ListDrilldown,
    ListHeader,
    ListTitle,
} from "@/components/ui/list";
import { routeLoginReport } from "../../routes";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { getPastRangeStart, type PastRange, useTimelineFormatting } from "@/lib/time";
import { useTranslation } from "react-i18next";
import { useFetchDashboardFleetStatus } from "@/lib/hooks/dashboard";
import {
    useFleetAuthenticationPeriods,
    useFleetAuthenticationPeriodsMetadata,
} from "@/lib/hooks/fleet-authentication";
import { FleetAuthenticatePeriodSortKey, SortDirection } from "../../api";
import { observer } from "mobx-react";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Empty, EmptyMedia, EmptyHeader, EmptyDescription } from "@/components/ui/empty";

const ListLogins = observer(({ range }: { range: DateRange }): JSX.Element => {
    const { t } = useTranslation();
    const viewportRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(0);
    const [periods] = useFleetAuthenticationPeriods({
        dateStart: range.from!,
        dateEnd: range.to!,
        sortDirection: SortDirection.DESCENDING,
        sortKey: FleetAuthenticatePeriodSortKey.LOGGED_IN_TIME,
        page,
    });

    useEffect(() => {
        setPage(0);
    }, [range]);

    const [eventsMetaData] = useFleetAuthenticationPeriodsMetadata({
        dateStart: range.from!,
        dateEnd: range.to!,
    });

    useEffect(() => {
        if (viewportRef.current) {
            viewportRef.current.scrollTop = 0;
        }
    }, [page]);

    if (periods === undefined || periods.length === 0) {
        return (
            <Empty className="min-h-80">
                <EmptyHeader>
                    <EmptyMedia variant="icon">
                        <ContactIcon />
                    </EmptyMedia>
                    <EmptyDescription>
                        {t("page.dashboard.fleetStatusTab.logins.empty")}
                    </EmptyDescription>
                </EmptyHeader>
            </Empty>
        );
    }

    return (
        <div className="relative flex h-full flex-col overflow-hidden">
            <ScrollArea viewportRef={viewportRef} className="flex flex-col">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                {t("page.dashboard.fleetStatusTab.logins.header.truck")}
                            </TableHead>
                            <TableHead>
                                {t("page.dashboard.fleetStatusTab.logins.header.operator")}
                            </TableHead>
                            <TableHead>
                                {t("page.dashboard.fleetStatusTab.logins.header.loginTime")}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {periods.map((row) => {
                            const user = store.repositoryUsers.byId(row.userId);
                            const vehicle = store.repositoryVehicles.byId(row.vehicleId);

                            return (
                                <TableRow key={row.id}>
                                    <TableCell padded>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span>{vehicle?.serialNumber}</span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {vehicle?.manufacturer} {vehicle?.model}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        {user?.firstName} {user?.lastName}
                                    </TableCell>
                                    <TableCell>
                                        {store.i18n.formatDateTime(row.startTime)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </ScrollArea>
            <div className="mt-auto p-4">
                <Pagination className="justify-end">
                    <PaginationContent>
                        <PaginationItem>
                            <PaginationPrevious
                                className={cn(
                                    "cursor-pointer",
                                    page <= 0 ? "pointer-events-none opacity-50" : undefined,
                                )}
                                label={t("component.pagination.previous")}
                                onClick={() => setPage(page - 1)}
                            />
                        </PaginationItem>
                        <PaginationItem className="font-medium">{`${page + 1} / ${eventsMetaData?.totalPages}`}</PaginationItem>
                        <PaginationItem>
                            <PaginationNext
                                className={cn(
                                    "cursor-pointer",
                                    page >= (eventsMetaData?.totalPages ?? 0) - 1
                                        ? "pointer-events-none opacity-50"
                                        : undefined,
                                )}
                                label={t("component.pagination.next")}
                                onClick={() => setPage(page + 1)}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            </div>
        </div>
    );
});

export type DashboardFleetStatusTabProps = {
    range: DateRange;
};

export const DashboardFleetStatusTab = ({ range }: DashboardFleetStatusTabProps): JSX.Element => {
    const { t } = useTranslation();
    const pastRange = useMemo(() => getPastRangeStart(range), [range]);
    const [data] = useFetchDashboardFleetStatus({
        dateStart: range.from!,
        dateEnd: range.to!,
        previousDateStart: pastRange.start,
        previousDateEnd: range.from!,
    });

    const formatTimelineDate = useTimelineFormatting(range);
    const formatTrendDescription = (
        trend: number | undefined | null,
        pastRange: PastRange,
    ): string => {
        if (trend == null || trend === 0.0) {
            return "";
        }

        const percentage = Math.round(trend * 100);
        const sign = trend > 0 ? "+" : "";
        return `${sign}${percentage}% ${store.i18n.formatPastRange(pastRange)}`;
    };

    const totalTrucks =
        (data?.activeTrucks ?? 0) +
        (data?.maintenanceDueTrucks ?? 0) +
        (data?.maintenanceOverdueTrucks ?? 0) +
        (data?.inMaintenanceTrucks ?? 0);

    return (
        <div>
            <div className="grid w-full grid-cols-12 gap-4">
                <StatisticCard
                    className="col-span-6 min-h-[133px] xl:col-span-3"
                    header={t("page.dashboard.fleetStatusTab.stat.operatorsLoggedIn")}
                    description={formatTrendDescription(data?.operatorsLoggedInTrend, pastRange)}
                    headerIcon={Users}
                    trend={data?.operatorsLoggedInTrend ? data.operatorsLoggedInTrend : undefined}
                >
                    {data?.operatorsLoggedIn}
                </StatisticCard>
                <StatisticCard
                    className="col-span-6 min-h-[133px] xl:col-span-3"
                    header={t("page.dashboard.fleetStatusTab.stat.drivingHours")}
                    description={formatTrendDescription(data?.driveTimeTrend, pastRange)}
                    headerIcon={Forklift}
                    trend={data?.driveTimeTrend ? data.driveTimeTrend : undefined}
                >
                    {data?.driveTime && Math.trunc(data.driveTime / 3600)}
                </StatisticCard>
                <StatisticCard
                    className="col-span-6 min-h-[133px] xl:col-span-3"
                    header={t("page.dashboard.fleetStatusTab.stat.expiredOperatorsLicenses")}
                    description={
                        data?.expiringOperatorLicenses
                            ? `${data?.expiringOperatorLicenses} licenses expiring soon`
                            : null
                    }
                    headerIcon={TriangleAlert}
                >
                    {data?.expiredOperatorLicenses}
                </StatisticCard>
                <StatisticCard
                    className="col-span-6 min-h-[133px] xl:col-span-3"
                    header={t("page.dashboard.fleetStatusTab.stat.truckLeasesExpiring")}
                    description={t(
                        "page.dashboard.fleetStatusTab.stat.truckLeasesExpiring.description",
                    )}
                    headerIcon={TriangleAlert}
                >
                    {data?.expiringTruckLeases}
                </StatisticCard>
                <Card className="col-span-12 flex max-h-80 flex-col p-4 xl:col-span-4">
                    <CardTitle className="text-base">
                        {t("page.dashboard.fleetStatusTab.truckBreakdown.title")}
                    </CardTitle>
                    <CardDescription>
                        {t("page.dashboard.fleetStatusTab.truckBreakdown.description")}
                    </CardDescription>
                    <div className="flex h-full min-h-48 items-center justify-center">
                        {data && (
                            <StandardPieChart
                                center={totalTrucks.toString()}
                                centerLabel={t(
                                    "page.dashboard.fleetStatusTab.truckBreakdown.chartLine",
                                )}
                                config={{
                                    active: {
                                        label: t(
                                            "page.dashboard.fleetStatusTab.truckBreakdown.active",
                                        ),
                                        color: "var(--color-primary)",
                                    },
                                    in_maintenance: {
                                        label: t(
                                            "page.dashboard.fleetStatusTab.truckBreakdown.inMaintenance",
                                        ),
                                        color: "var(--color-neutral-300)",
                                    },
                                    maintenance_due: {
                                        label: t(
                                            "page.dashboard.fleetStatusTab.truckBreakdown.maintenanceDue",
                                        ),
                                        color: "var(--color-red-500)",
                                    },
                                    maintenance_overdue: {
                                        label: t(
                                            "page.dashboard.fleetStatusTab.truckBreakdown.maintenanceOverdue",
                                        ),
                                        color: "var(--color-red-700)",
                                    },
                                }}
                                data={[
                                    {
                                        key: "active",
                                        data: data.activeTrucks,
                                    },
                                    {
                                        key: "in_maintenance",
                                        data: data.inMaintenanceTrucks,
                                    },
                                    {
                                        key: "maintenance_due",
                                        data: data.maintenanceDueTrucks,
                                    },
                                    {
                                        key: "maintenance_overdue",
                                        data: data.maintenanceOverdueTrucks,
                                    },
                                ]}
                            />
                        )}
                    </div>
                </Card>
                <List className="col-span-12 row-span-2 max-h-[606px] xl:col-span-8">
                    <ListHeader>
                        <div>
                            <ListTitle className="text-base">
                                {t("page.dashboard.fleetStatusTab.logins.title")}
                            </ListTitle>
                            <ListDescription>
                                {t("page.dashboard.fleetStatusTab.logins.description")}
                            </ListDescription>
                        </div>
                        <ListDrilldown to={routeLoginReport} />
                    </ListHeader>
                    <ListContent className="flex-grow">
                        <ListLogins range={range} />
                    </ListContent>
                </List>
                <Card className="col-span-12 flex h-80 flex-col p-4 xl:col-span-4">
                    <CardTitle className="mb-1.5">
                        {t("page.dashboard.fleetStatusTab.truckUtilization.title")}
                    </CardTitle>
                    <CardDescription>
                        {t("page.dashboard.fleetStatusTab.truckUtilization.description")}
                    </CardDescription>
                    <div className="flex h-full items-center justify-center">
                        {data && data.truckUtilizationChartData.length > 0 && (
                            <StandardTimelineChart
                                label={t(
                                    "page.dashboard.fleetStatusTab.truckUtilization.dataPoint",
                                )}
                                valueFormatter={(value) => `${value}%`}
                                data={data.truckUtilizationChartData.map((point) => ({
                                    t: formatTimelineDate(point.bucket),
                                    data: Math.round(point.utilization * 100),
                                }))}
                            />
                        )}
                        {(!data || data.truckUtilizationChartData.length === 0) && (
                            <div className="h-full w-full pt-4">
                                <Empty>
                                    <EmptyHeader>
                                        <EmptyMedia variant="icon">
                                            <ChartLineIcon />
                                        </EmptyMedia>
                                        <EmptyDescription>
                                            {t(
                                                "page.dashboard.fleetStatusTab.truckUtilization.empty",
                                            )}
                                        </EmptyDescription>
                                    </EmptyHeader>
                                </Empty>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
};
